'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import {
  type FilterableProductType,
  PRODUCT_TYPE_QUERY_KEY,
  resolveProductType,
} from '@/helpers/products-filter-type'

interface HeaderContent {
  title: string
  description: string
}

const DEFAULT_HEADER: HeaderContent = {
  title: 'Cursos Online',
  description:
    'Nossos cursos são focados em você. Sinta-se em casa e inscreva-se em quantos desejar, tudo foi feito pensando em você.',
}

const HEADER_BY_PRODUCT_TYPE: Partial<
  Record<FilterableProductType, HeaderContent>
> = {
  postgraduate: {
    title: 'Pós-graduação',
    description:
      'Aprofunde seus conhecimentos e fortaleça sua atuação profissional. Encontre a pós-graduação que combina com seus objetivos e dê o próximo passo na sua carreira.',
  },
  track: {
    title: 'Trilha',
    description:
      'Siga uma sequência de cursos que se complementam e aprofunde seus conhecimentos. Escolha a trilha que combina com seus objetivos e avance no seu aprendizado.',
  },
  webinar: {
    title: 'Webinars',
    description:
      'Explore temas relevantes para sua prática com especialistas da área. Escolha os encontros que despertam seu interesse e descubra novas ideias para aplicar no dia a dia.',
  },
}

function HeaderLayout({ title, description }: Readonly<HeaderContent>) {
  return (
    <header className="space-y-4 border-primary-100 pt-12 md:flex md:flex-wrap md:items-center md:justify-between md:border-b-2 md:pb-6">
      <h1 className="font-bold text-4.5xl text-gray-900 md:text-4xl">
        {title}
      </h1>

      <div className="md:max-w-109.75">
        <p className="font-normal text-gray-500 text-sm leading-4">
          {description}
        </p>
      </div>
    </header>
  )
}

export function ProductsHeaderFallback() {
  return <HeaderLayout {...DEFAULT_HEADER} />
}

export function ProductsHeader() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const slug = pathname.split('/').filter(Boolean)[1]
  const productType = resolveProductType({
    slug,
    type: searchParams.get(PRODUCT_TYPE_QUERY_KEY),
  })

  return (
    <HeaderLayout
      {...(HEADER_BY_PRODUCT_TYPE[productType] ?? DEFAULT_HEADER)}
    />
  )
}
